import React, { useCallback, useEffect, useState } from 'react';
import { RxDotFilled } from 'react-icons/rx';

import styled from 'styled-components';

import Badge from 'components/common/Badge';

import { getVariantTabStyle } from 'styles/components/getVariantStyle';
import { typography } from 'styles/token/typography';

/**
 * @desc 탭 컴포넌트
 * @param {Object[]} tabs - 탭 목록
 *    @example {title: '개발 문의', header: true, content: {…}}
 *    @param {string} tabs[].title - 탭 제목
 *    @param {JSX.Element} tabs[].content - 탭 내용
 *  @param {number} [tabItem=0] - 초기 선택된 탭 인덱스
 * @param {JSX.Element} [tabHeader] - 탭 헤더 추가 컴포넌트
 * @param {function} [tabOnClick] - 탭 클릭 시 실행할 함수
 * @returns {JSX.Element} 탭 컴포넌트
 */
const Tab = ({
  tabs,
  tabItem = 0,
  tabHeader,
  tabOnClick,
  className,
  variant = 'boxed', // 'boxed' | 'underlined' | 'full'
}) => {
  const [activeTab, setActiveTab] = useState(tabItem);

  // tabItem 프롭 변경 시 내부 상태 동기화
  useEffect(() => {
    if (tabItem !== undefined && tabItem !== -1) {
      setActiveTab(tabItem);
    }
  }, [tabItem]);

  const changeTab = useCallback(
    (index, title, disabled) => {
      if (disabled) return;
      setActiveTab(index);
      if (tabOnClick) {
        tabOnClick(index, title);
      }
    },
    [tabOnClick]
  );

  return (
    <TabContainer className={`${className || ''} tab_container ${variant}`}>
      <TabHeader className="tab_header">
        <TabBox className="tab_box" $variant={variant}>
          {tabs &&
            tabs.map((tab, index) => (
              <TabItem
                key={tab.title}
                className={`${String(index) === String(activeTab) ? 'active' : ''} ${
                  tab.disabled ? 'disabled' : ''
                }`}
                onClick={() => changeTab(index, tab.title, tab.disabled)}
              >
                {tab.title} {''}
                {tab.count !== undefined && (
                  <span className="tab_count">
                    {tab.count.toLocaleString()}
                  </span>
                )}
                {tab.badge && (
                  <Badge
                    color="red700"
                    className="tab_badge"
                    icon={<RxDotFilled />}
                  />
                )}
              </TabItem>
            ))}
        </TabBox>
        {tabs[activeTab]?.header && tabHeader}
      </TabHeader>
      <TabContent className="tab_content">
        {tabs[activeTab]?.content}
      </TabContent>
    </TabContainer>
  );
};
// 탭 컨테이너 스타일
const TabContainer = styled.div``;

// 탭 헤더 스타일
export const TabHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray100};

  @media screen and (max-width: 768px) {
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: 8px;
    .tab_container.boxed & {
      margin-bottom: 26px;
    }
  }
`;

// 탭 박스 스타일
export const TabBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;

  ${({ $variant }) => getVariantTabStyle($variant)}
`;

// 탭 스타일
export const TabItem = styled.div`
  cursor: pointer;
  text-align: center;
  transition: all 0.2s;
  position: relative;

  .tab_badge {
    position: absolute;
    top: 1px;
    right: 6px;
  }
`;

// 탭 컨텐츠 스타일
const TabContent = styled.div``;
const TabMapResult = styled(Tab)`
  ${TabItem} {
    ${typography.titleExtraSmall};
    font-size: 13px;
    padding: 12px 16px;
  }
`;
const TabConsole = styled(Tab)`
  margin-top: 24px;
`;

Tab.MapResult = TabMapResult;
Tab.Console = TabConsole;

export default Tab;
